import type { RefObject } from "react";
import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { useSingleFlight } from "@/hooks/useSingleFlight";
import { recordSpan } from "@/services/telemetry";
import { store } from "@/store/store";
import { ComposerDraftStore } from "./composerDraftStore";
import {
  type ComposerAttachment,
  type ComposerDraft,
  type ComposerUpload,
  composerDraftSendable,
  sendComposerDraft,
} from "./composerSend";
import type { DispatchTableRow } from "./dispatchTableRow";
import { rowFeedId } from "./dispatchTableRow";
import type { ReplyTrigger } from "./dispatchTracking";
import { useComposerUpload } from "./useComposerUpload";

/** What a `DispatchComposer` renders and drives: the draft for the current channel and the callbacks that change it. */
export type ComposerState = {
  draft: ComposerDraft;
  onTextChange: (text: string) => void;
  /** A file was chosen or pasted; it waits in the draft and uploads on Send. Replaces one already waiting. */
  onAttach: (attachment: ComposerAttachment) => void;
  /** Drops the waiting file without sending it. */
  onDetach: () => void;
  /** Publishes the draft; `trigger` names how Send was pressed, for what follows the send. */
  send: (trigger: ReplyTrigger) => Promise<void>;
  sending: boolean;
  error: string | null;
  inputRef: RefObject<HTMLTextAreaElement | HTMLInputElement>;
  /** Puts the cursor in the input on the next frame, since the input may enable only after a render. */
  focus: () => void;
};

/**
 * The part of a composer that holds still while its draft is typed: where the drafts live, the send, and the input.
 *
 * The component that owns the composer keeps this and hands it down; whatever renders the box reads the draft from
 * it with {@link useComposerState}, so a keystroke re-renders that component and nothing above it.
 */
export type ComposerHandle = {
  drafts: ComposerDraftStore;
  /** Publishes the current draft; presses that land while a send is in flight do nothing. */
  send: (trigger: ReplyTrigger) => Promise<void>;
  inputRef: RefObject<HTMLTextAreaElement | HTMLInputElement>;
  /** Puts the cursor in the input on the next frame, since the input may enable only after a render. */
  focus: () => void;
};

/**
 * Sends the draft kept under `key` to `feedId`: text and file through `sendComposerDraft`, then drops the draft.
 *
 * The draft is read from the store at call time, which is what lets the send keep one identity while it is typed. The
 * text leaves the draft as soon as it is published, so a send that fails in the file's upload keeps only the file for
 * the next try. A send that fails records a reliability span, as the fleet composer does, so a run of failures on the
 * page's main action shows in aggregate, and leaves the draft and a message for the composer to show.
 *
 * @param drafts - Where the draft is kept, and where the send's progress is recorded for the composer to render.
 * @param key - What the draft is kept under.
 * @param feedId - The channel the draft goes to.
 * @param workspaceId - Empty while the page has no workspace, and nothing is sent.
 * @param spanAttributes - What the send's span records beyond its timing.
 * @param upload - The upload the draft's file goes through.
 * @returns Whether the draft went out. A draft with nothing to send, or a send already in flight, is not tried.
 */
async function publishDraft(
  drafts: ComposerDraftStore,
  key: string,
  feedId: string,
  workspaceId: string,
  spanAttributes: Record<string, unknown>,
  upload: ComposerUpload,
): Promise<boolean> {
  const draft = drafts.draft(key);
  if (!workspaceId || drafts.getStatus().sending || !composerDraftSendable(draft)) return false;
  drafts.setStatus({ sending: true, error: null });
  const startedAt = new Date();
  const attributes = { dispatchReply: { ...spanAttributes, hasAttachment: draft.attachment != null } };
  try {
    await sendComposerDraft({
      workspaceId,
      feedId,
      draft,
      isSilent: store.get("feed", feedId)?.isSilent,
      upload,
      onTextPublished: () => drafts.updateDraft(key, (current) => ({ ...current, text: "" })),
    });
    recordSpan("dispatch_reply.send", startedAt, attributes);
    drafts.clearDraft(key);
    drafts.setStatus({ sending: false, error: null });
    return true;
  } catch {
    recordSpan("dispatch_reply.send_failed", startedAt, attributes, "reliability", { isError: true });
    drafts.setStatus({ sending: false, error: "Failed to send. Try again." });
    return false;
  }
}

/**
 * The handle over `drafts` whose send runs `reply`, single-flighted.
 *
 * `useSingleFlight` holds the latch in a ref, so a press landing before React commits `sending` still finds the send
 * in flight and is dropped.
 */
function useComposerHandle(
  drafts: ComposerDraftStore,
  reply: (trigger: ReplyTrigger) => Promise<void>,
): ComposerHandle {
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const send = useSingleFlight(reply);
  const focus = useCallback(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);
  return useMemo(() => ({ drafts, send, inputRef, focus }), [drafts, send, focus]);
}

/**
 * What a `DispatchComposer` renders for the draft under `key`, read from the handle's store.
 *
 * The component calling this is the one a keystroke re-renders; the one that owns the handle is not.
 *
 * @param handle - The composer's store, send, and input, from whoever owns the drafts.
 * @param key - What the draft is kept under; with none, the box shows the empty draft and typing keeps nothing.
 */
export function useComposerState(handle: ComposerHandle, key: string | null): ComposerState {
  const { drafts, send, inputRef, focus } = handle;
  const draft = useSyncExternalStore(drafts.subscribe, () => drafts.draft(key));
  const { sending, error } = useSyncExternalStore(drafts.subscribe, drafts.getStatus);

  const update = useCallback(
    (change: (current: ComposerDraft) => ComposerDraft) => {
      if (key) drafts.updateDraft(key, change);
    },
    [drafts, key],
  );
  const onTextChange = useCallback((text: string) => update((current) => ({ ...current, text })), [update]);
  const onAttach = useCallback(
    (attachment: ComposerAttachment) => update((current) => ({ ...current, attachment })),
    [update],
  );
  const onDetach = useCallback(() => update((current) => ({ ...current, attachment: null })), [update]);

  return useMemo(
    () => ({ draft, onTextChange, onAttach, onDetach, send, sending, error, inputRef, focus }),
    [draft, onTextChange, onAttach, onDetach, send, sending, error, inputRef, focus],
  );
}

/**
 * A composer's state for whichever channel `key` names: a draft (text and a waiting file) per key, so moving between
 * channels never loses what was typed or attached, and the send that publishes the current draft to `feedId`.
 *
 * `send` publishes text and file through `sendComposerDraft` and drops the draft. The drafts are the caller's own, so
 * its component re-renders as it is typed.
 *
 * @param key - What the draft is kept under: the channel in the thread.
 * @param feedId - The channel the current draft goes to; null while there is none, and `send` does nothing.
 * @param workspaceId - Empty while the page has no workspace, and `send` does nothing.
 * @param spanAttributes - What the send's span records beyond its timing.
 */
export function useComposer({
  key,
  feedId,
  workspaceId,
  spanAttributes,
  onSent,
}: {
  key: string | null;
  feedId: string | null;
  workspaceId: string;
  spanAttributes: Record<string, unknown>;
  /** What follows a send, as it stood when Send was pressed, told how Send was pressed. */
  onSent?: (trigger: ReplyTrigger) => void;
}): ComposerState {
  const [drafts] = useState(() => new ComposerDraftStore());
  const upload = useComposerUpload();
  // The send reads the channel, and what follows it, when it is pressed, so it keeps one identity while the channel's
  // draft is typed.
  const latest = useRef({ key, feedId, workspaceId, spanAttributes, onSent, upload });
  latest.current = { key, feedId, workspaceId, spanAttributes, onSent, upload };
  const reply = useCallback(
    async (trigger: ReplyTrigger) => {
      const current = latest.current;
      if (!current.key || !current.feedId) return;
      const sent = await publishDraft(
        drafts,
        current.key,
        current.feedId,
        current.workspaceId,
        current.spanAttributes,
        current.upload,
      );
      if (sent) current.onSent?.(trigger);
    },
    [drafts],
  );
  return useComposerState(useComposerHandle(drafts, reply), key);
}

/** What the inline reply's draft for `row` is kept under: the driver, so each of their rows shares one draft. */
export function dispatchDraftKey(row: DispatchTableRow): string {
  return row.driver.workspaceMembershipId;
}

/**
 * The inline reply's handle: a draft per driver, kept by the page for as long as it is open, and the send that
 * publishes the highlighted row's draft to its channel.
 *
 * The handle holds one identity while drafts are typed and the highlight moves, so the page never re-renders for a
 * keystroke; the reply under the row reads its draft with {@link useComposerState} and {@link dispatchDraftKey}.
 * `onSent` gets the row that was replied to, the channel the reply went to, and how Send was pressed, where the page
 * takes the keyboard back. The send itself writes no acknowledgement: `onSent` is where the page acknowledges the row
 * the reply answered.
 *
 * @param highlighted - The row a press of Send replies to.
 * @param workspaceId - Empty while the page has no workspace, and `send` does nothing.
 * @param onSent - What follows a reply: the row replied to, the channel it went to, and how Send was pressed.
 */
export function useDispatchComposer({
  highlighted,
  workspaceId,
  onSent,
}: {
  highlighted: DispatchTableRow | null;
  workspaceId: string;
  onSent: (row: DispatchTableRow, feedId: string, trigger: ReplyTrigger) => void;
}): ComposerHandle {
  const [drafts] = useState(() => new ComposerDraftStore());
  const upload = useComposerUpload();
  const latest = useRef({ highlighted, workspaceId, onSent, upload });
  latest.current = { highlighted, workspaceId, onSent, upload };
  const reply = useCallback(
    async (trigger: ReplyTrigger) => {
      // The row a send answers, and what follows it, are the ones current when Send was pressed, whatever the
      // highlight does meanwhile. The latch around this runs it only for a press it lets through, so a dropped press
      // never names a row.
      const { highlighted: replied, workspaceId: currentWorkspaceId, onSent: afterSend, upload: send } = latest.current;
      const feedId = replied ? rowFeedId(replied) : null;
      if (!replied || !feedId) return;
      const spanAttributes = { source: "dispatch", grain: replied.kind };
      const key = dispatchDraftKey(replied);
      const sent = await publishDraft(drafts, key, feedId, currentWorkspaceId, spanAttributes, send);
      if (sent) afterSend(replied, feedId, trigger);
    },
    [drafts],
  );
  return useComposerHandle(drafts, reply);
}
